import Badge from '@/components/ui/Badge'
import Overview from '@/components/ui/Overview'
import { cn } from '@/lib/utils'
import {
  Recommendation,
  recommendationLabel,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type { Category } from '@/payload-types'

type RecommendationBadgeProps = {
  recommendation: Recommendation
  efficiencyPoints?: { points: number; total: number }
  category?: Pick<Category, 'categoryCriteria' | 'name' | 'slug' | 'id' | 'icon'>
  className?: string
}

export default function RecommendationBadge({
  recommendation,
  efficiencyPoints,
  category,
  className,
}: RecommendationBadgeProps) {
  if (recommendation !== Recommendation.TopPick && recommendation !== Recommendation.Recommended) {
    return null
  }

  const recommendationBadge: Record<Recommendation, 'primary' | 'outline' | 'default'> = {
    [Recommendation.TopPick]: 'primary',
    [Recommendation.Recommended]: 'outline',
    [Recommendation.Capable]: 'default',
    [Recommendation.NotRecommended]: 'default',
  } as const satisfies Record<Recommendation, 'primary' | 'outline' | 'default'>

  const hasRatings = efficiencyPoints && efficiencyPoints.total > 0
  const hasDescription =
    efficiencyPoints && typeof efficiencyPoints.points === 'number' && Boolean(category?.name)
  const description = hasDescription ? (
    <div
      className="w-max flex-col text-balance rounded-xl border border-white/20 bg-black px-3 py-2 text-center text-12 leading-relaxed text-white shadow-md"
      style={
        hasRatings
          ? undefined
          : {
              maxWidth: `${Math.ceil(`Not yet rated, but meets the general efficiency bar in the ${category?.name} category`.length / 2) - 2}ch`,
            }
      }
    >
      <p>
        {hasRatings ? (
          <>
            Meets the efficiency bar in {efficiencyPoints.points}{' '}
            {efficiencyPoints.total ? `of ${efficiencyPoints.total} ` : ''}
            <br />
            criteria in the {category?.name} category
          </>
        ) : (
          <>Not yet rated, but meets the general efficiency bar in the {category?.name} category</>
        )}
      </p>
    </div>
  ) : null

  const badge = (
    <Badge
      style={recommendationBadge[recommendation]}
      className={cn('capitalize transition-transform duration-300 ease-in-out', className)}
      icon={
        recommendation === Recommendation.TopPick || recommendation === Recommendation.Recommended
          ? 'best'
          : undefined
      }
      nested
    >
      <span className="hidden sm:block">{recommendationLabel[recommendation]}</span>
    </Badge>
  )

  return description ? <Overview content={description}>{badge}</Overview> : badge
}
